import DOMPurify from 'dompurify';

interface SanitizationOptions {
  allowHtml?: boolean;
  maxLength?: number;
  trimWhitespace?: boolean;
}

export const sanitizeInput = (
  input: string, 
  options: SanitizationOptions = {}
): string => {
  const {
    allowHtml = false,
    maxLength = 1000,
    trimWhitespace = true
  } = options;

  if (!input || typeof input !== 'string') {
    return '';
  }

  let sanitized = input;

  // Trim whitespace if enabled
  if (trimWhitespace) {
    sanitized = sanitized.trim();
  }

  // Truncate to max length
  if (maxLength > 0 && sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  // Sanitize HTML content
  if (!allowHtml) {
    // Strip all HTML tags and entities
    sanitized = DOMPurify.sanitize(sanitized, { 
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: []
    });
  } else {
    // Allow basic HTML but sanitize dangerous content
    sanitized = DOMPurify.sanitize(sanitized, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br'],
      ALLOWED_ATTR: []
    });
  }

  return sanitized;
};

// List of valid email domain extensions (common TLDs)
const VALID_EMAIL_TLDS = [
  // Generic TLDs
  'com', 'net', 'org', 'edu', 'gov', 'mil', 'co', 'io', 'ai', 'app', 'dev', 'info', 'biz', 'me', 'tv', 'cc',
  // Country codes - Oceania & Pacific
  'au', 'nz', 'fj', 'pg',
  // Country codes - Europe
  'uk', 'de', 'fr', 'it', 'es', 'nl', 'be', 'ch', 'at', 'se', 'no', 'dk', 'fi', 'pl', 'ie', 'pt', 'gr', 'cz', 'ro', 'hu',
  // Country codes - Americas
  'us', 'ca', 'mx', 'br', 'ar', 'cl', 'co', 'pe',
  // Country codes - Asia
  'jp', 'cn', 'kr', 'sg', 'in', 'hk', 'tw', 'th', 'my', 'ph', 'id', 'vn', 'pk', 'bd', 'lk', 'np', 'mm', 'kh', 'la',
  // Country codes - Middle East & Africa
  'ae', 'sa', 'il', 'tr', 'eg', 'za', 'ng', 'ke', 'gh', 'ma',
  // Compound TLDs - Australia
  'com.au', 'edu.au', 'gov.au', 'org.au', 'net.au',
  // Compound TLDs - UK
  'co.uk', 'org.uk', 'ac.uk', 'gov.uk',
  // Compound TLDs - Asia Pacific
  'co.nz', 'co.in', 'com.sg', 'com.my', 'com.ph', 'co.id', 'co.th', 'com.pk', 'com.bd', 'com.hk', 'com.tw', 'com.vn',
  // Compound TLDs - Other
  'co.za', 'com.br', 'com.mx', 'co.jp', 'co.kr'
];

// Common email providers for additional validation
const COMMON_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com', 
  'icloud.com', 'me.com', 'aol.com', 'protonmail.com', 'mail.com',
  'msn.com', 'ymail.com', 'googlemail.com', 'fastmail.com', 'zoho.com'
];

// Known legitimate short domains (exceptions to minimum length rule)
const KNOWN_SHORT_DOMAINS = [
  't.co', 'fb.com', 'g.co', 'x.com', 'qq.com', 'wp.pl', 'wp.com',
  'me.com', 'go.com', 'goo.gl', 'bit.ly', 'ow.ly',
  'aj.com.au', 'ey.com', 'ge.com', 'hp.com', 'lg.com', 'bp.com'
];

export const validateEmailFormat = (email: string): { isValid: boolean; error?: string } => {
  const sanitized = email.trim().toLowerCase();
  
  // Check basic structure
  const basicEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!basicEmailRegex.test(sanitized)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  
  // Extract domain parts
  const [localPart, domain] = sanitized.split('@');
  
  // Validate local part length
  if (localPart.length < 1 || localPart.length > 64) {
    return { isValid: false, error: 'Email username is too long or too short' };
  }
  
  // Validate domain length
  if (domain.length < 4 || domain.length > 255) {
    return { isValid: false, error: 'Email domain is invalid' };
  }
  
  // Check for consecutive dots
  if (sanitized.includes('..')) {
    return { isValid: false, error: 'Email cannot contain consecutive dots' };
  }
  
  // Extract TLD from domain
  const domainParts = domain.split('.');
  if (domainParts.length < 2) {
    return { isValid: false, error: 'Email domain must include a valid extension' };
  }
  
  // Get TLD (handle compound TLDs like com.au)
  const lastPart = domainParts[domainParts.length - 1];
  const secondLastPart = domainParts.length >= 3 ? domainParts[domainParts.length - 2] : null;
  const compoundTld = secondLastPart ? `${secondLastPart}.${lastPart}` : null;
  
  // Check if it's a valid TLD
  const hasValidTld = VALID_EMAIL_TLDS.includes(lastPart) || 
                      (compoundTld && VALID_EMAIL_TLDS.includes(compoundTld));
  
  if (!hasValidTld) {
    return { isValid: false, error: 'Please enter a valid email domain (e.g., gmail.com, outlook.com)' };
  }
  
  // Check minimum domain name length (before TLD)
  // Skip this check for compound TLDs (like aj.com.au) where the structure itself validates the domain
  const domainNamePart = domainParts[0]; // The part before the TLD
  const isKnownShortDomain = KNOWN_SHORT_DOMAINS.includes(domain);
  const hasCompoundTld = compoundTld && VALID_EMAIL_TLDS.includes(compoundTld);
  
  if (!isKnownShortDomain && !hasCompoundTld && domainNamePart.length < 3) {
    return { isValid: false, error: 'Email domain name is too short. Please use a valid email provider.' };
  }
  
  // Check for common typos in popular domains (exact match only, not substring)
  const commonTypos: Record<string, string> = {
    'gmial.com': 'gmail.com', 'gmal.com': 'gmail.com', 'gmaill.com': 'gmail.com', 'gmali.com': 'gmail.com',
    'gamil.com': 'gmail.com', 'gnail.com': 'gmail.com', 'gmaul.com': 'gmail.com', 'gmil.com': 'gmail.com',
    'hotmal.com': 'hotmail.com', 'hotmial.com': 'hotmail.com', 'hotamil.com': 'hotmail.com',
    'outlok.com': 'outlook.com', 'outllok.com': 'outlook.com', 'outlokk.com': 'outlook.com',
    'yahooo.com': 'yahoo.com', 'yaho.com': 'yahoo.com', 'yhoo.com': 'yahoo.com', 'yaoo.com': 'yahoo.com'
  };
  
  // Only flag if the entire domain matches a known typo (not substring matching)
  if (commonTypos[domain]) {
    return { isValid: false, error: `Did you mean ${commonTypos[domain]}? Please check your email domain.` };
  }
  
  return { isValid: true };
};

export const sanitizeEmail = (email: string): string => {
  const sanitized = sanitizeInput(email, { maxLength: 254, trimWhitespace: true });
  
  const validation = validateEmailFormat(sanitized);
  if (!validation.isValid) {
    return '';
  }
  
  return sanitized.toLowerCase();
};

export const isValidEmail = (email: string): boolean => {
  return validateEmailFormat(email).isValid;
};

export const sanitizePhone = (phone: string): string => {
  // Remove all non-digit characters except + and spaces
  let sanitized = phone.replace(/[^\d\s+()-]/g, '');
  
  // Trim and limit length
  sanitized = sanitized.trim();
  if (sanitized.length > 20) {
    sanitized = sanitized.substring(0, 20);
  }
  
  return sanitized;
};

export const sanitizeName = (name: string): string => {
  // Allow letters, spaces, hyphens, and apostrophes
  let sanitized = name.replace(/[^a-zA-Z\s'-]/g, '');
  
  return sanitizeInput(sanitized, { 
    maxLength: 100, 
    trimWhitespace: true 
  });
};

export const sanitizeMessage = (message: string): string => {
  return sanitizeInput(message, { 
    maxLength: 2000, 
    trimWhitespace: true,
    allowHtml: false
  });
};

// Form data sanitization
export const sanitizeFormData = (formData: Record<string, any>): Record<string, any> => {
  const sanitized: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(formData)) {
    if (typeof value === 'string') {
      switch (key) {
        case 'email':
          sanitized[key] = sanitizeEmail(value);
          break;
        case 'phone':
          sanitized[key] = sanitizePhone(value);
          break;
        case 'name':
        case 'firstName':
        case 'lastName':
          sanitized[key] = sanitizeName(value);
          break;
        case 'message':
        case 'description':
        case 'content':
          sanitized[key] = sanitizeMessage(value);
          break;
        default:
          sanitized[key] = sanitizeInput(value);
      }
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};